// historicalDataService.js
// Singleton service for historical data management
import Papa from 'papaparse';
import { useState, useEffect } from 'react';

// Module-level variables for singleton pattern
let historicalListingsData = null;
let processedData = null;
let isDataLoading = false;
let dataLoadPromise = null;

/**
 * Process raw historical listings into a structured format
 * This includes grouping, sorting, and handling special cases for years
 */
const processHistoricalData = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  // Process historical data
  
  // Sort all listings by close date (newest first)
  const sortedListings = [...rawData].sort((a, b) => {
    const dateA = Date.parse(a['Close Date'] || '') || 0;
    const dateB = Date.parse(b['Close Date'] || '') || 0;
    return dateB - dateA;
  });

  // Group listings by year
  const grouped = {};
  
  sortedListings.forEach(listing => {
    if (!listing['Close Date']) return; // Skip entries with no close date
    
    const date = new Date(listing['Close Date']);
    if (isNaN(date)) return; // Skip entries with invalid dates
    
    let year = date.getFullYear();
    if (isNaN(year) || year < 1900 || year > 2100) return; // Skip entries with invalid years
    
    // Merge 2025 listings into 2024 (as per LAST YEAR requirement)
    if (year === 2025) year = 2024;
    
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year].push(listing);
  });

  // Sort years in descending order (newest first)
  const sortedYears = Object.keys(grouped).sort((a, b) => b - a);
  
  // Sort listings within each year by date (newest first)
  sortedYears.forEach(year => {
    // For 2024 (LAST YEAR), make sure 2025 listings come first, then sort by date
    if (year === '2024') {
      grouped[year].sort((a, b) => {
        const dateA = new Date(a['Close Date']);
        const dateB = new Date(b['Close Date']);
        const yearA = dateA.getFullYear();
        const yearB = dateB.getFullYear();
        
        // If one is from 2025 and the other isn't, prioritize 2025
        if (yearA === 2025 && yearB !== 2025) return -1;
        if (yearA !== 2025 && yearB === 2025) return 1;
        
        // Otherwise sort by date (newest first)
        return dateB - dateA;
      });
    } else {
      // Normal sorting for other years
      grouped[year].sort((a, b) => {
        return new Date(b['Close Date']) - new Date(a['Close Date']);
      });
    }
  });

  // Data is now processed

  return {
    raw: sortedListings,
    grouped: grouped,
    sortedYears: sortedYears
  };
};

/**
 * Load historical listings data from CSV
 * This will only fetch the data once regardless of how many components call it
 */
export async function loadHistoricalData() {
  // If already loading or data is already loaded, return the existing promise or data
  if (isDataLoading) {
    return dataLoadPromise;
  }
  
  if (historicalListingsData) {
    return historicalListingsData;
  }
  
  isDataLoading = true;

  // Start new data load
  dataLoadPromise = fetch('/data/historicalListings.csv')
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to load historical listings');
      }
      return response.text();
    })
    .then(csvText => {
      const { data } = Papa.parse(csvText, { header: true });
      
      // Basic validation
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format: Expected array');
      }
      
      // Store the raw data
      historicalListingsData = data;
      
      // Process the data immediately
      processedData = processHistoricalData(data);
      
      // Historical data loaded and processed successfully
      return historicalListingsData;
    })
    .catch(error => {
      console.error('Failed to load historical data:', error);
      // Reset flags on error
      isDataLoading = false;
      throw error;
    })
    .finally(() => {
      isDataLoading = false;
    });

  return dataLoadPromise;
};

/**
 * Get raw historical listings data
 * Will load the data if not already loaded
 */
export const getHistoricalListings = async () => {
  if (!historicalListingsData) {
    await loadHistoricalData();
  }
  return historicalListingsData;
};

/**
 * Get processed historical listings data
 * Will process the data if not already processed
 */
export const getProcessedHistoricalListings = async () => {
  if (!historicalListingsData) {
    await loadHistoricalData();
  }
  
  // If data hasn't been processed yet, process it now
  if (!processedData) {
    processedData = processHistoricalData(historicalListingsData);
  }
  
  return processedData;
};

/**
 * React hook to use historical listings data
 */
export const useHistoricalListings = () => {
  const [listings, setListings] = useState(historicalListingsData || []);
  const [processed, setProcessed] = useState(processedData || null);
  const [loading, setLoading] = useState(!historicalListingsData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!historicalListingsData) {
      setLoading(true);
      loadHistoricalData()
        .then(data => {
          setListings(data);
          setProcessed(processedData);
          setLoading(false);
        })
        .catch(err => {
          setError(err);
          setLoading(false);
        });
    }
  }, []);

  return { 
    listings,
    processed,
    loading, 
    error 
  };
};
